import { ComponentProps, forwardRef } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Fade, styled, TextField as MuiTextField } from '@mui/material'

export type TextFieldProps = Omit<ComponentProps<typeof MuiTextField>, 'variant' | 'round'> & {
  round?: boolean
}
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({ round, ...props }, ref) => (
  <StyledOutlineTextField
    variant="outlined"
    inputRef={ref}
    $round={round}
    fullWidth
    size="small"
    margin="normal"
    InputLabelProps={{
      shrink: true,
      sx: {
        display: 'flex',
        alignItems: 'center',
      },
    }}
    SelectProps={{
      MenuProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        TransitionComponent: Fade,
      },
    }}
    {...props}
  />
))


const StyledOutlineTextField = styled(MuiTextField, {
  shouldForwardProp: (prop) => prop !== '$round',
}) <{ $round: boolean | undefined }>`
  width: 100%;
  border-radius: ${(props) => props.$round ? props.theme.typography.pxToRem(1000) : props.theme.typography.pxToRem(8)};
  boder: ${(props) => props.theme.palette.custom.input.border};
  margin-top: ${(props) => props.theme.typography.pxToRem(8)};
  
  & .MuiInputLabel-root {
    font-size: ${(props) => props.theme.typography.pxToRem(12)};
    font-weight: 600;
    position: relative;
    transform: none;
    margin-bottom: ${(props) => props.theme.typography.pxToRem(3)};
  }

  .MuiOutlinedInput-root {
    font-size: ${(props) => props.theme.typography.pxToRem(13)};
    border-radius: ${(props) => props.$round ? '1000px' : props.theme.typography.pxToRem(4)};
    background-color: ${(props) => props.theme.palette.custom.input.backgroundColor};
    padding: 0;

    & .MuiAutocomplete-input {
      padding: 8px 0 8px 8px !important;
    }

    input {
      height: unset;
      line-height: normal;
      padding: 8px;
    }

    & .MuiSelect-outlined {
      min-height: unset;
      height: unset;
      line-height: normal;
      padding: 8px 32px 8px 8px;
    }
  }

  .MuiOutlinedInput-notchedOutline {
    top: 0;
    left: 0;
    > legend {
      display: none; 
    }
  }

  .MuiSvgIcon-root {
    color: ${(props) => props.theme.palette.text.primary};
  }
  
  .MuiInputAdornment-root {
    .MuiButtonBase-root {
      margin: ${pxToRem(7)} ${pxToRem(8)};
    }
  }

  .Mui-focused fieldset {
    box-shadow: 0 0 0 2px ${(props) => props.theme.palette.background.paper}, 0 0 0 3px ${(props) => props.theme.palette.custom.button.focusedOutline};
  }

  .Mui-error.Mui-focused fieldset {
    box-shadow: 0 0 0 2px ${(props) => props.theme.palette.background.paper}, 0 0 0 3px rgba(211, 47, 47, 0.4);    
  }

  .Mui-disabled {
    .MuiSvgIcon-root {
      color: ${(props) => props.theme.palette.text.disabled};
    }
  }

  .MuiInputBase-multiline {
    height: unset;

    textarea {
      padding: 8px 0 8px 8px;
    }
  }

  .MuiFormHelperText-root {
    margin-left: 0;
  }
`
